import { useState, useEffect, useCallback } from 'react';
import { getEnumValues, ENUM_NAMES, type EnumValue } from '../services/enumValues';

interface UseEnumOptions {
  defaultValues?: EnumValue[];
  cacheDuration?: number;
}

interface UseEnumReturn {
  values: EnumValue[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  getDisplayName: (code: string) => string;
}

/**
 * React hook for fetching and using enum values
 * @param enumName - The name of the enum to fetch
 * @param options - Optional configuration
 * @returns Object with enum values, loading state, error, and refresh function
 * 
 * @example
 * ```tsx
 * const { values: reminderTypes, loading, getDisplayName } = useEnum(ENUM_NAMES.REMINDER_TYPES);
 * 
 * // In JSX:
 * <Select>
 *   {reminderTypes.map(type => (
 *     <MenuItem key={type.code} value={type.code}>{type.display_name}</MenuItem>
 *   ))}
 * </Select>
 * ```
 */
export function useEnum(enumName: string, options: UseEnumOptions = {}): UseEnumReturn {
  const { defaultValues = [] } = options;
  
  const [values, setValues] = useState<EnumValue[]>(defaultValues);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchValues = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getEnumValues(enumName, forceRefresh);
      setValues(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load enum values');
      // Keep existing values on error
    } finally {
      setLoading(false);
    }
  }, [enumName]);

  useEffect(() => {
    fetchValues();
  }, [fetchValues]);

  const refresh = useCallback(async () => {
    await fetchValues(true);
  }, [fetchValues]);

  const getDisplayName = useCallback(
    (code: string) => {
      const match = values.find(v => v.code === code);
      return match?.display_name || code;
    },
    [values]
  );

  return {
    values,
    loading,
    error,
    refresh,
    getDisplayName,
  };
}

// Re-export ENUM_NAMES for convenience
export { ENUM_NAMES };
